import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, HStack, Text, Badge, Checkbox, useToast, Tabs,
  TabList, Tab, TabPanels, TabPanel, NumberInput, NumberInputField,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { AudienciaJuzgado, TipoAudiencia } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'new' | 'edit' | 'view';
  audiencia: AudienciaJuzgado | null;
  onGuardado: (a: AudienciaJuzgado) => void;
}

const TIPOS: TipoAudiencia[] = ['Calificación','Mediación','Conciliación','Revisión de sanción','Apelación'];
const ESTATUSES = ['Programada','Reprogramada','Cancelada','En espera de citación','Celebrada','No compareció','Cerrada'];

const EMPTY: Partial<AudienciaJuzgado> = {
  tipoAudiencia: 'Calificación',
  horaInicio: '09:00',
  estatus: 'Programada',
  esWhatsapp: false,
  cancelado: false,
};

export default function ModalAudiencia({ isOpen, onClose, mode, audiencia, onGuardado }: Props) {
  const [form, setForm] = useState<Partial<AudienciaJuzgado>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const readOnly = mode === 'view';

  const set = (k: keyof AudienciaJuzgado, v: any) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) setForm(audiencia ? { ...audiencia } : { ...EMPTY });
  }, [isOpen, audiencia]);

  const handleGuardar = async () => {
    if (!form.expedienteRef && !form.causaId) return toast({ title: 'Se requiere expediente o causa', status: 'warning' });
    if (!form.fechaProgramada) return toast({ title: 'Fecha de audiencia requerida', status: 'warning' });
    if (!form.horaInicio) return toast({ title: 'Hora de inicio requerida', status: 'warning' });

    setSaving(true);
    try {
      let resultado: AudienciaJuzgado;
      if (mode === 'new') {
        resultado = await juzgadoApi.crearAudiencia(form);
        toast({ title: `Audiencia programada: ${resultado.folioAudiencia}`, status: 'success' });
      } else {
        resultado = await juzgadoApi.actualizarAudiencia(audiencia!.id, form);
        toast({ title: 'Audiencia actualizada', status: 'success' });
      }
      onGuardado(resultado);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          <HStack>
            <Text>{mode === 'new' ? 'Nueva Audiencia' : mode === 'edit' ? 'Editar Audiencia' : 'Audiencia'}</Text>
            {audiencia?.folioAudiencia && <Badge colorScheme="yellow">{audiencia.folioAudiencia}</Badge>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody pt={4}>
          <Tabs colorScheme="red" variant="enclosed">
            <TabList>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Programación</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Participantes</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Contacto</Tab>
            </TabList>
            <TabPanels>

              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Número de expediente</FormLabel>
                      <Input value={form.expedienteRef ?? ''} onChange={e => set('expedienteRef', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Tipo de audiencia</FormLabel>
                      <Select value={form.tipoAudiencia ?? ''} onChange={e => set('tipoAudiencia', e.target.value as TipoAudiencia)} isDisabled={readOnly} borderColor="#bb8c52">
                        {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Fecha programada</FormLabel>
                      <Input type="date" value={form.fechaProgramada ? String(form.fechaProgramada).substring(0, 10) : ''} onChange={e => set('fechaProgramada', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Hora de inicio</FormLabel>
                      <Input type="time" value={form.horaInicio ?? ''} onChange={e => set('horaInicio', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Hora estimada de término</FormLabel>
                      <Input type="time" value={form.horaEstimadaTermino ?? ''} onChange={e => set('horaEstimadaTermino', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Estatus</FormLabel>
                      <Select value={form.estatus ?? ''} onChange={e => set('estatus', e.target.value as any)} isDisabled={readOnly} borderColor="#bb8c52">
                        {ESTATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </Select>
                    </FormControl>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Sala del juzgado</FormLabel>
                      <Input value={form.lugarSala ?? ''} onChange={e => set('lugarSala', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Sala virtual (link)</FormLabel>
                      <Input value={form.lugarVirtualLink ?? ''} onChange={e => set('lugarVirtualLink', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Centro externo</FormLabel>
                      <Input value={form.lugarExterno ?? ''} onChange={e => set('lugarExterno', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel fontSize="sm">Observaciones</FormLabel>
                    <Textarea value={form.observaciones ?? ''} onChange={e => set('observaciones', e.target.value)} isReadOnly={readOnly} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Juez asignado</FormLabel>
                    <Input value={form.juezAsignado ?? ''} onChange={e => set('juezAsignado', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Secretario de acuerdos</FormLabel>
                    <Input value={form.secretarioAcuerdos ?? ''} onChange={e => set('secretarioAcuerdos', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Escribiente</FormLabel>
                    <Input value={form.escribiente ?? ''} onChange={e => set('escribiente', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Custodia / Policía asignado</FormLabel>
                    <Input value={form.custodia ?? ''} onChange={e => set('custodia', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Imputado / Detenido</FormLabel>
                    <Input value={form.imputado ?? ''} onChange={e => set('imputado', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Víctima</FormLabel>
                    <Input value={form.victima ?? ''} onChange={e => set('victima', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Representante legal</FormLabel>
                    <Input value={form.representanteLegal ?? ''} onChange={e => set('representanteLegal', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

              <TabPanel px={0}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Teléfono de contacto</FormLabel>
                    <Input value={form.telefonoContacto ?? ''} onChange={e => set('telefonoContacto', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" mt={6}>
                      <Checkbox
                        isChecked={form.esWhatsapp ?? false}
                        onChange={e => set('esWhatsapp', e.target.checked)}
                        isDisabled={readOnly}
                        colorScheme="red"
                      >
                        Es también WhatsApp
                      </Checkbox>
                    </FormLabel>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                    <Input type="email" value={form.correoContacto ?? ''} onChange={e => set('correoContacto', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Dirección de citación</FormLabel>
                    <Input value={form.direccionCitacion ?? ''} onChange={e => set('direccionCitacion', e.target.value)} isReadOnly={readOnly} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter borderTopWidth={1}>
          <Button variant="ghost" mr={3} onClick={onClose}>Cerrar</Button>
          {!readOnly && (
            <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} isLoading={saving} onClick={handleGuardar}>
              {mode === 'new' ? 'Programar Audiencia' : 'Guardar Cambios'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
